import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/handball";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export function GoalkeeperDialog({ open, onOpenChange, onCreate }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await onCreate(name.trim());
    setSaving(false);
    setName("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#111] border-[#27272A] text-white"
        data-testid="goalkeeper-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-widest text-xl">
            Ny målvakt
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Lägg till en ny målvakt att registrera statistik för.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Label className="text-zinc-400 uppercase tracking-widest text-xs">
            Namn
          </Label>
          <Input
            data-testid="goalkeeper-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="T.ex. Emma Andersson"
            className="bg-black border-[#3F3F46] text-white focus-visible:ring-[#34C759]"
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
        </div>
        <DialogFooter>
          <Button
            data-testid="goalkeeper-save-btn"
            onClick={submit}
            disabled={saving || !name.trim()}
            className="bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest"
          >
            Spara
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SessionDialog({ open, onOpenChange, onCreate, goalkeeperId }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("match");
  const [opponentMode, setOpponentMode] = useState("__none__");
  const [opponentText, setOpponentText] = useState("");
  const [prevOpponents, setPrevOpponents] = useState([]);
  const [oppStats, setOppStats] = useState(null);
  const [oppLoading, setOppLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tournaments, setTournaments] = useState([]);
  const [tournamentId, setTournamentId] = useState("__none__");

  useEffect(() => {
    if (!open || !goalkeeperId) return;
    (async () => {
      try {
        const { data } = await api.get("/opponents", {
          params: { goalkeeper_id: goalkeeperId },
        });
        setPrevOpponents(data);
      } catch {
        setPrevOpponents([]);
      }
      // Fetch tournaments that are active today (end_date >= today).
      // Pass for_goalkeeper so shared editors get the owner's tournaments.
      try {
        const today = new Date().toISOString().slice(0, 10);
        const res = await api.get("/tournaments", {
          params: { active_on: today, for_goalkeeper: goalkeeperId },
        });
        setTournaments(res.data);
      } catch {
        setTournaments([]);
      }
      setTournamentId("__none__");
    })();
  }, [open, goalkeeperId]);

  // Fetch opponent stats when user picks an existing opponent
  useEffect(() => {
    if (
      !goalkeeperId ||
      opponentMode === "__none__" ||
      opponentMode === "__new__"
    ) {
      setOppStats(null);
      return;
    }
    setOppLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/opponent-stats", {
          params: { goalkeeper_id: goalkeeperId, opponent: opponentMode },
        });
        setOppStats(data);
      } catch {
        setOppStats(null);
      } finally {
        setOppLoading(false);
      }
    })();
  }, [goalkeeperId, opponentMode]);

  const submit = async () => {
    if (!name.trim() || !goalkeeperId) return;
    setSaving(true);
    const opponent =
      type !== "match"
        ? null
        : opponentMode === "__none__"
        ? null
        : opponentMode === "__new__"
        ? opponentText.trim() || null
        : opponentMode;
    await onCreate({
      name: name.trim(),
      session_type: type,
      opponent,
      goalkeeper_id: goalkeeperId,
      tournament_id: tournamentId === "__none__" ? null : tournamentId,
    });
    setSaving(false);
    setName("");
    setOpponentText("");
    setOpponentMode("__none__");
    setType("match");
    setTournamentId("__none__");
    onOpenChange(false);
  };

  const isMatch = type === "match";
  const isNewOpponent = opponentMode === "__new__";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#111] border-[#27272A] text-white"
        data-testid="session-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-widest text-xl">
            Ny session
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Skapa en match eller träning för att registrera skott.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label className="text-zinc-400 uppercase tracking-widest text-xs">
              Namn
            </Label>
            <Input
              data-testid="session-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="T.ex. Hemmamatch 2026-02-10"
              className="bg-black border-[#3F3F46] text-white focus-visible:ring-[#34C759]"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-400 uppercase tracking-widest text-xs">
              Typ
            </Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger
                data-testid="session-type-select"
                className="bg-black border-[#3F3F46] text-white"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#111] border-[#3F3F46] text-white">
                <SelectItem value="match">Match</SelectItem>
                <SelectItem value="traning">Träning</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {tournaments.length > 0 && (
            <div className="space-y-2">
              <Label className="text-zinc-400 uppercase tracking-widest text-xs">
                Inkludera i turnering/serie (valfritt)
              </Label>
              <Select value={tournamentId} onValueChange={setTournamentId}>
                <SelectTrigger
                  data-testid="session-tournament-select"
                  className="bg-black border-[#3F3F46] text-white"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-[#3F3F46] text-white max-h-[280px]">
                  <SelectItem value="__none__">Ingen</SelectItem>
                  {tournaments.map((t) => (
                    <SelectItem
                      key={t.id}
                      value={t.id}
                      data-testid={`session-tournament-option-${t.id}`}
                    >
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {isMatch && (
            <div className="space-y-2">
              <Label className="text-zinc-400 uppercase tracking-widest text-xs">
                Motståndare
              </Label>
              <Select value={opponentMode} onValueChange={setOpponentMode}>
                <SelectTrigger
                  data-testid="session-opponent-select"
                  className="bg-black border-[#3F3F46] text-white"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-[#3F3F46] text-white max-h-[280px]">
                  <SelectItem value="__none__">Ingen motståndare</SelectItem>
                  {prevOpponents.length > 0 && (
                    <>
                      {prevOpponents.map((op) => (
                        <SelectItem
                          key={op}
                          value={op}
                          data-testid={`opponent-option-${op}`}
                        >
                          {op}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  <SelectItem value="__new__">+ Ny motståndare</SelectItem>
                </SelectContent>
              </Select>
              {isNewOpponent && (
                <Input
                  data-testid="session-opponent-input"
                  value={opponentText}
                  onChange={(e) => setOpponentText(e.target.value)}
                  placeholder="T.ex. IFK Kristianstad"
                  autoFocus
                  className="bg-black border-[#3F3F46] text-white focus-visible:ring-[#34C759] mt-2"
                />
              )}

              {/* Opponent analysis */}
              {opponentMode !== "__none__" && opponentMode !== "__new__" && (
                <div
                  data-testid="opponent-analysis"
                  className="mt-3 p-3 border border-[#27272A] bg-[#0a0a0a]"
                >
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display mb-2">
                    Motståndaranalys
                  </div>
                  {oppLoading ? (
                    <div className="text-xs text-zinc-500">Laddar…</div>
                  ) : oppStats && oppStats.matches_played > 0 && oppStats.stats ? (
                    <div className="space-y-2">
                      <div className="text-xs text-zinc-300">
                        <span data-testid="opp-matches" className="font-display font-bold">
                          {oppStats.matches_played}
                        </span>{" "}
                        tidigare match{oppStats.matches_played === 1 ? "" : "er"} ·{" "}
                        <span data-testid="opp-save-pct" className="text-[#34C759] font-mono-num font-bold">
                          {oppStats.stats.save_pct.toFixed(0)}%
                        </span>{" "}
                        räddning ({oppStats.stats.saves}/{oppStats.stats.total})
                      </div>
                      {oppStats.worst_placements?.length > 0 && (
                        <div className="text-[11px] text-zinc-400" data-testid="opp-weak-placements">
                          <span className="text-zinc-500">Svaga rutor:</span>{" "}
                          {oppStats.worst_placements.slice(0, 3).map((p, i) => (
                            <span key={p.placement} className="text-white">
                              {i > 0 ? ", " : ""}#{p.placement}{" "}
                              <span className="text-[#FF3B30] font-mono-num">
                                {Math.round(p.save_pct)}%
                              </span>
                            </span>
                          ))}
                        </div>
                      )}
                      {oppStats.worst_zones?.length > 0 && (
                        <div className="text-[11px] text-zinc-400" data-testid="opp-weak-zones">
                          <span className="text-zinc-500">Svaga zoner:</span>{" "}
                          {oppStats.worst_zones.slice(0, 3).map((z, i) => (
                            <span key={z.zone} className="text-white">
                              {i > 0 ? ", " : ""}{z.zone}{" "}
                              <span className="text-[#FF3B30] font-mono-num">
                                {Math.round(z.save_pct)}%
                              </span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-xs text-zinc-500" data-testid="opp-nodata">
                      Ingen historik mot denna motståndare ännu.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            data-testid="session-save-btn"
            onClick={submit}
            disabled={
              saving ||
              !name.trim() ||
              !goalkeeperId ||
              (isMatch && isNewOpponent && !opponentText.trim())
            }
            className="bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest"
          >
            Spara
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * SessionEditDialog
 *   Redigera metadata på en befintlig session: namn, typ, motståndare,
 *   turnering, slutresultat, kommentar.
 */
export function SessionEditDialog({ open, onOpenChange, session, onSaved }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("match");
  const [opponent, setOpponent] = useState("");
  const [tournamentId, setTournamentId] = useState("__none__");
  const [tournaments, setTournaments] = useState([]);
  const [finalScore, setFinalScore] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !session) return;
    setName(session.name || "");
    setType(session.session_type || "match");
    setOpponent(session.opponent || "");
    setTournamentId(session.tournament_id || "__none__");
    setFinalScore(session.final_score || "");
    setNotes(session.notes || "");
    (async () => {
      try {
        // Show ALL tournaments (including past) so user can retroactively assign.
        const { data } = await api.get("/tournaments", {
          params: { for_goalkeeper: session.goalkeeper_id },
        });
        setTournaments(data);
      } catch {
        setTournaments([]);
      }
    })();
  }, [open, session]);

  const submit = async () => {
    if (!session) return;
    if (!name.trim()) {
      toast.error("Namn krävs");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        session_type: type,
        opponent: type === "match" ? (opponent.trim() || null) : null,
        tournament_id: tournamentId === "__none__" ? null : tournamentId,
        final_score: finalScore.trim() || null,
        notes: notes.trim() || null,
      };
      const { data } = await api.patch(`/sessions/${session.id}`, payload);
      toast.success("Match uppdaterad");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  if (!session) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#111] border-[#27272A] text-white max-h-[90vh] overflow-y-auto"
        data-testid="session-edit-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-widest text-xl">
            Redigera match
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Ändra namn, typ, motståndare, turnering, slutresultat eller anteckning.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label className="text-zinc-400 uppercase tracking-widest text-xs">Namn</Label>
            <Input
              data-testid="session-edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-black border-[#3F3F46] text-white focus-visible:ring-[#34C759]"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-400 uppercase tracking-widest text-xs">Typ</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="session-edit-type" className="bg-black border-[#3F3F46] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#111] border-[#3F3F46] text-white">
                <SelectItem value="match">Match</SelectItem>
                <SelectItem value="traning">Träning</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {type === "match" && (
            <div className="space-y-2">
              <Label className="text-zinc-400 uppercase tracking-widest text-xs">Motståndare</Label>
              <Input
                data-testid="session-edit-opponent"
                value={opponent}
                onChange={(e) => setOpponent(e.target.value)}
                placeholder="Ex. IFK Kristianstad"
                className="bg-black border-[#3F3F46] text-white focus-visible:ring-[#34C759]"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-zinc-400 uppercase tracking-widest text-xs">
              Turnering/serie
            </Label>
            <Select value={tournamentId} onValueChange={setTournamentId}>
              <SelectTrigger data-testid="session-edit-tournament" className="bg-black border-[#3F3F46] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#111] border-[#3F3F46] text-white max-h-[280px]">
                <SelectItem value="__none__">Ingen</SelectItem>
                {tournaments.map((t) => (
                  <SelectItem
                    key={t.id}
                    value={t.id}
                    data-testid={`session-edit-tournament-option-${t.id}`}
                  >
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-400 uppercase tracking-widest text-xs">Slutresultat</Label>
            <Input
              data-testid="session-edit-score"
              value={finalScore}
              onChange={(e) => setFinalScore(e.target.value)}
              placeholder="Ex. 24-22"
              className="bg-black border-[#3F3F46] text-white focus-visible:ring-[#34C759]"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-400 uppercase tracking-widest text-xs">Kommentar</Label>
            <Textarea
              data-testid="session-edit-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="bg-black border-[#3F3F46] text-white focus-visible:ring-[#34C759]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            data-testid="session-edit-save-btn"
            onClick={submit}
            disabled={saving || !name.trim()}
            className="bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest"
          >
            Spara ändringar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

